/**
 * RequestContext - Request-scoped context management via AsyncLocalStorage
 *
 * Provides correlation ID and user context that is automatically available
 * throughout the request lifecycle without explicit parameter passing.
 *
 * Usage:
 * - Initialize at request entry (via withRequestContext or guards)
 * - Update with user info after authentication
 * - Logger automatically reads and includes in log output
 *
 * Note: This module is safe to import in client code - all functions become
 * no-ops in browser environment since AsyncLocalStorage is Node.js only.
 */

export interface RequestContextData {
  /** Unique identifier for correlating all logs from a single request */
  correlationId: string;
  /** User ID from session (set after auth) */
  userId?: string;
  /** User email from session (set after auth) */
  userEmail?: string;
  /** Request path for context */
  path?: string;
  /** Request start timestamp for duration tracking */
  startTime: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Environment Detection & Lazy Loading
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Detect if we're in a Node.js environment (not browser, not Edge runtime).
 * Edge runtime has `globalThis.EdgeRuntime` set.
 */
function isNodeServer(): boolean {
  // Browser check
  if (typeof window !== 'undefined') return false;
  // Edge runtime check (Vercel Edge, Cloudflare Workers, etc.)
  if (typeof (globalThis as any).EdgeRuntime !== 'undefined') return false;
  // Deno check
  if (typeof (globalThis as any).Deno !== 'undefined') return false;
  return true;
}

function isEdgeRuntime(): boolean {
  return typeof (globalThis as any).EdgeRuntime !== 'undefined';
}

const canUseAsyncHooks = isNodeServer();

// Warn once if running in Edge - helps catch misconfigured routes
let edgeWarningLogged = false;
function warnIfEdge(operation: string): void {
  if (isEdgeRuntime() && !edgeWarningLogged) {
    edgeWarningLogged = true;
    console.warn(
      `[RequestContext] ${operation} called in Edge runtime - context tracking disabled. ` +
      `Add 'export const runtime = "nodejs"' to your route if you need request context.`
    );
  }
}

// Lazy-load AsyncLocalStorage only on server to avoid client bundling issues
type AsyncLocalStorageType<T> = {
  run<R>(store: T, callback: () => R): R;
  getStore(): T | undefined;
};

let asyncLocalStorage: AsyncLocalStorageType<RequestContextData> | null = null;

function getAsyncLocalStorage(): AsyncLocalStorageType<RequestContextData> | null {
  if (!canUseAsyncHooks) return null;

  if (!asyncLocalStorage) {
    // Dynamic require with webpack ignore to avoid client bundling
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { AsyncLocalStorage } = /* webpackIgnore: true */ require('node:async_hooks') as {
      AsyncLocalStorage: new () => AsyncLocalStorageType<RequestContextData>;
    };
    asyncLocalStorage = new AsyncLocalStorage();
  }

  return asyncLocalStorage;
}

// Lazy-load crypto.randomUUID only on Node server
function generateCorrelationId(): string {
  if (canUseAsyncHooks) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { randomUUID } = /* webpackIgnore: true */ require('node:crypto');
    return randomUUID();
  }
  // Fallback for browser/Edge (use Web Crypto API if available)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Last resort fallback
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a new request context with optional initial data.
 * Generates a new correlation ID if not provided.
 */
export function createRequestContext(data: Partial<RequestContextData> = {}): RequestContextData {
  return {
    correlationId: data.correlationId ?? generateCorrelationId(),
    userId: data.userId,
    userEmail: data.userEmail,
    path: data.path,
    startTime: data.startTime ?? Date.now(),
  };
}

/**
 * Execute a function within a request context scope.
 * All code within the function (including async operations) will have access to the context.
 * No-op on client side or Edge runtime (with warning).
 */
export function runWithContext<T>(context: RequestContextData, fn: () => T): T {
  const als = getAsyncLocalStorage();
  if (!als) {
    warnIfEdge('runWithContext');
    return fn();
  }
  return als.run(context, fn);
}

/**
 * Get the current request context, or undefined if not in a context.
 * Always returns undefined on client side.
 */
export function getRequestContext(): RequestContextData | undefined {
  const als = getAsyncLocalStorage();
  return als?.getStore();
}

/**
 * Get just the correlation ID from current context.
 */
export function getCorrelationId(): string | undefined {
  return getRequestContext()?.correlationId;
}

/**
 * Get just the user ID from current context.
 */
export function getUserId(): string | undefined {
  return getRequestContext()?.userId;
}

/**
 * Update the current request context with additional data.
 * Commonly used to add user info after authentication completes.
 * No-op on client side or when not in a context.
 */
export function updateRequestContext(updates: Partial<RequestContextData>): void {
  const current = getRequestContext();
  if (current) {
    Object.assign(current, updates);
  }
}

/**
 * Get request duration in milliseconds.
 */
export function getRequestDuration(): number | undefined {
  const ctx = getRequestContext();
  return ctx ? Date.now() - ctx.startTime : undefined;
}
